import {Component, ElementRef, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {CalendarOptions} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {CompanyEventCalendarManagerService} from "./company-event-calendar-manager.service";
import { Subscription } from "rxjs";
import {Apollo} from "apollo-angular";
import {get_company_event_dates} from "../graphql/getCompanyEvents";
import {CurrencyPipe, DatePipe, formatCurrency} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {
  AddCompanyEventModalComponent
} from "../../components/add-company-event-modal/add-company-event-modal.component";
import {FullCalendarComponent} from "@fullcalendar/angular";
import {DateTimeService} from "../../services/date-time.service";
import {CompanyEventsListManagerService} from "../company-events-list/company-events-list-manager.service";
import CompanyEventDate from "../../interfaces/company-event-date";

@Component({
    selector: 'company-events-calendar',
    templateUrl: './company-events-calendar.component.html',
    styleUrls: ['./company-events-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyEventsCalendarComponent implements OnInit{
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  start:string = ""
  end: string = ""
  tooltip: HTMLElement;
  permissions: any;
  outlookEventType: any;
  outlookCalendar: any;
  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    dateClick: (arg) => {},
    events: [],
    themeSystem: 'bootstrap',
    eventMouseEnter: this.hoverEvent.bind(this),
    eventMouseLeave: this.leaveEvent.bind(this),
    customButtons: {
      addNewEventBtn: {
        icon: 'fc-icon-plus-square',
        hint: "Add new event",
        click: () => this.addEventAction()
      },
      listBtn: {
        text: "LIST",
        hint: "Go to list view",
        click: () => this.toList()
      },
      exportBtn: {
        text: '↷',
        hint: 'Export',
        click: () => {this.exportCalendar()}
      }
    },
    headerToolbar: {
      start: '',
      center: 'title',
      end: 'today prev,next listBtn exportBtn',
    },
    aspectRatio: 1.8,
    height: 'auto',
    eventClick: this.eventClick.bind(this),
    displayEventTime: false,
    eventDisplay: 'block',
    expandRows: true,
    // initialDate: new Date(2024,10,4),
    datesSet: this.monthRender.bind(this),
    eventContent: (info) => this.renderEvent(info),
    plugins: [dayGridPlugin, interactionPlugin,],
  };
  public exportableEventDates: CompanyEventDate[] = []


  constructor(public companyEventCalendarManager: CompanyEventCalendarManagerService, private renderer: Renderer2, private el: ElementRef, private apollo: Apollo, private datePipe: DatePipe, private dateTime: DateTimeService, private route: ActivatedRoute, private router: Router,
              private dialog: MatDialog, private companyEventListManager: CompanyEventsListManagerService) {
  }


  ngOnInit() {
    let queryDates = this.route.snapshot.queryParamMap.get('start')
    if(queryDates!==null){
      this.calendarOptions.initialDate = new Date(queryDates)
    }

  }

  monthRender(arg:any){
    let start = arg.start.toISOString().slice(0,10);
    let end = arg.end.toISOString().slice(0,10);
    this.getCalendarEventDates(start, end);
  }



  renderEvent(arg:any){
    let event = arg.event;
    let modifiers = ['is_sponsored', 'is_cancelled', 'is_cosponsored', 'is_postponed'];
    let modifierCount = 0;
    for(const [key, value] of Object.entries(event.extendedProps)){
      if(modifiers.includes(key)){
        if(value==true){
          modifierCount += 1
        }
      }
    }
    let customHTML = `<div class="fc-event-main-frame" id="${event.extendedProps.eventNum}">`;
    customHTML += `<div class="fc-event-title-container"><div class="fc-event-title fc-sticky`
    if(modifierCount==1){
      customHTML += ` fc-m-1`
    }
    if(modifierCount==2){
      customHTML += ` fc-m-2`
    }
    if(modifierCount==3){
      customHTML += ` fc-m-3`
    }
    if(modifierCount==4){
      customHTML += ` fc-m-4`
    }
    customHTML += `">${event.title}</div>`

    if(event.extendedProps.is_sponsored){
      customHTML += `<i class="material-icons in-line-icon">diamond</i>`
    }
    if(event.extendedProps.is_cosponsored){
      customHTML += `<i class="material-icons in-line-icon">groups</i>`
    }
    if(event.extendedProps.is_cancelled){
      customHTML += `<i class="material-icons in-line-icon target-danger">warning</i>`
    }
    if(event.extendedProps.is_postponed){
      customHTML += `<i class="material-icons in-line-icon postpone">warning</i>`
    }
    customHTML += `</div>`
    customHTML += `</div>`

    return {html: customHTML}

  }

  hoverEvent(arg:any){
    if(this.tooltip){
      return ;
    }
    this.tooltip = this.renderer.createElement('div');
    // ----------- Adding title to tooltip -------------------------------
    let title = this.renderer.createElement('div')
    this.renderer.addClass(title, 'tooltip-title')
    this.renderer.appendChild(
      title, this.renderer.createText(arg.event.title)
    )
    this.renderer.appendChild(this.tooltip, title)
    this.renderer.appendChild(this.tooltip, this.renderer.createElement('hr'))

    // -------------- Adding type details -------------------------------
    let details = this.renderer.createElement('div')
    this.renderer.addClass(details, 'tooltip-details')
    let type = this.renderer.createElement('div')
    this.renderer.addClass(type, 'tooltip-subtitle')
    this.renderer.appendChild(type, this.renderer.createText(arg.event.extendedProps.type))
    this.renderer.appendChild(details, type)
    this.renderer.appendChild(details, this.renderer.createElement('hr'))

    // ------------------ Dates and Times ------------------------------------
    let start = this.datePipe.transform(arg.event.startStr, 'shortDate')
    let end = this.datePipe.transform(arg.event.endStr, 'shortDate')
    let date = this.renderer.createElement('div')
    let dateText;
    if(start!==null && (end==null || end==start)){
      dateText = `${start}`
    }
    if(start!=null && end!==null && (end!==start)){
      dateText = `${start} - ${end}`
    }
    this.renderer.appendChild(date, this.renderer.createText(`Dates: ${dateText}`))
    this.renderer.appendChild(details, date)

    let time = this.renderer.createElement('div')
    let timeText;

    if(!!arg.event.extendedProps.startTime && !arg.event.extendedProps.endTime==null){
      timeText = `Times: ${arg.event.extendedProps.startTime}`
      if(arg.event.extendedProps.timezone!==null){
        timeText += ` ${arg.event.extendedProps.timezone}`
      }
    }
    if(!!arg.event.extendedProps.startTime && !!arg.event.extendedProps.endTime){
      timeText = `Times: ${arg.event.extendedProps.startTime} - ${arg.event.extendedProps.endTime}`
      if(arg.event.extendedProps.timezone!==null){
        timeText += ` ${arg.event.extendedProps.timezone}`
      }
    }
    if(!!arg.event.extendedProps.startTime){
        this.renderer.appendChild(time, this.renderer.createText(`${timeText}`))
        this.renderer.appendChild(details, time)
    }

    // ---------------- Properties -----------------------------------
    if(arg.event.extendedProps.is_sponsored){
      let property = this.renderer.createElement('div');
      let icon = this.renderer.createElement('i');
      this.renderer.addClass(icon, 'material-icons');
      this.renderer.appendChild(icon, this.renderer.createText('diamond'));
      this.renderer.appendChild(property, icon);
      this.renderer.appendChild(property, this.renderer.createText('CREA Sponsored'));
      this.renderer.appendChild(details, property);

    }

    if(arg.event.extendedProps.is_cancelled){
      let property = this.renderer.createElement('div')
      let icon = this.renderer.createElement('i')
      this.renderer.addClass(icon, 'material-icons')
      this.renderer.appendChild(icon, this.renderer.createText('warning'))
      this.renderer.appendChild(property, icon);
      this.renderer.appendChild(property, this.renderer.createText(` (Cancelled)`))
      this.renderer.appendChild(details, property)
    }

    if(arg.event.extendedProps.is_cosponsored){
      let property = this.renderer.createElement('div')
      let icon = this.renderer.createElement('i')
      this.renderer.addClass(icon, 'material-icons')
      this.renderer.appendChild(icon, this.renderer.createText('groups'))
      this.renderer.appendChild(property, icon);
      this.renderer.appendChild(property, this.renderer.createText(arg.event.extendedProps.cosponsor))
      this.renderer.appendChild(details, property)
    }

    if(arg.event.extendedProps.is_postponed){
      let property = this.renderer.createElement('div')
      let icon = this.renderer.createElement('i')
      this.renderer.addClass(icon, 'material-icons')
      this.renderer.appendChild(icon, this.renderer.createText('warning'))
      this.renderer.appendChild(property, icon);
      this.renderer.appendChild(property, this.renderer.createText(` (Postponed)`))
      this.renderer.appendChild(details, property)
    }
    if(arg.event.extendedProps.sponsorship_amount!==null){
      let property = this.renderer.createElement('div')
      this.renderer.appendChild(property, this.renderer.createText(`Sponsorship Amount: ${formatCurrency(arg.event.extendedProps.sponsorship_amount, 'en-US', '$', 'USD', '1.0-0')}`))
      this.renderer.appendChild(details, property)
    }
    if(arg.event.extendedProps.location.city!==null && arg.event.extendedProps.location.state!==null){
      let property = this.renderer.createElement('div')
      this.renderer.appendChild(property, this.renderer.createText(`Location: ${arg.event.extendedProps.location.city}, ${arg.event.extendedProps.location.state}`))
      this.renderer.appendChild(details, property)
    }


    // ------------------- Add details to tooltip element -----------------------------------
    this.renderer.appendChild(this.tooltip, details)

    // ----------------- Add tooltip to DOM and style -------------------------
    this.renderer.appendChild(document.body, this.tooltip)
    this.renderer.addClass(this.tooltip, 'tooltip')
    const elemRect = document.getElementById(`${arg.event.extendedProps.eventNum}`)?.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();

    let left, top;

    if(elemRect) {
      top = elemRect?.bottom + 10;
      left = elemRect?.left + (elemRect.width - tooltipRect.width)/2
    }

    this.renderer.setStyle(this.tooltip, 'top', `${top}px`)
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`)
    this.renderer.addClass(this.tooltip, `tooltip-bottom-${arg.event.extendedProps.type.toLowerCase().split(" ").join("-")}`);
    this.renderer.setStyle(this.tooltip, 'background-color', `${arg.event.backgroundColor}`)
    this.renderer.setStyle(this.tooltip, '-webkit-transition', `opacity 400ms`);
    this.renderer.setStyle(this.tooltip, '-moz-transition', `opacity 400ms`);
    this.renderer.setStyle(this.tooltip, '-o-transition', `opacity 400ms`);
    this.renderer.setStyle(this.tooltip, 'transition', `opacity 400ms`);
    this.renderer.addClass(this.tooltip, 'tooltip-show')

  }

  leaveEvent(arg:any){
    if(!this.tooltip) return;
    this.renderer.removeClass(this.tooltip, 'tooltip-show');
    if(this.tooltip){
      this.renderer.removeChild(document.body, this.tooltip)
      this.tooltip = null!
    }
  }

  exportCalendar(){
    let data = this.companyEventListManager.formatCompanyListForExport(this.exportableEventDates)
    this.companyEventListManager.exportCompanyEventList(data).then((result) => {})
  }

  getCalendarEventDates(start: string, end: string) {
    let querySubscription: Subscription
    this.start = start;
    this.end = end;
    querySubscription = this.apollo.watchQuery({
      query: get_company_event_dates,
      variables: {
        start: start,
        end: end
      },
      fetchPolicy: 'network-only'
    }).valueChanges.subscribe(({data}) =>
      {
        let eventResponse:any = data;
        let eventsArr:any[] = []
        this.permissions = eventResponse.me.permissionsForResource
        this.exportableEventDates = eventResponse.companyEventDates
        if(this.permissions._create){
          this.calendarOptions.headerToolbar = {
            start: '',
            center: 'title',
            end: 'today prev,next addNewEventBtn listBtn exportBtn'
          }
        }
        if(eventResponse.outlookEventTypes){
          this.outlookEventType = eventResponse.outlookEventTypes
          this.outlookCalendar = eventResponse.outlookEventTypes.outlookEventTypePreference.outlookCalendar
        }
        for(let i=0; i < eventResponse.companyEventDates.length; i++){
            let event = eventResponse.companyEventDates[i];
            let companyDatesArr = eventResponse.companyEventDates.filter((evnt:any) => evnt.companyEvent.id==event.companyEvent.id)

            let endTimeAppend = event.end_time ? event.end_time : '23:59:00';
            let formTime = this.dateTime.formatCompanyEventTimes(event);
            let startTime = formTime.viewStart.length>0 ? this.datePipe.transform(event.start_date + " " + formTime.viewStart, 'shortTime') : null
            let endTime = formTime.viewEnd.length>0 ? this.datePipe.transform((event.end_date!==null ? event.end_date : event.start_date) + " " + formTime.viewEnd, 'shortTime') : null
            let hasInactiveDate = companyDatesArr.length>1 && event.initial_date_time==0
            if(event.end_date==null && !hasInactiveDate){

              let eventObj:any = {
                title: event.companyEvent.title,
                start: event.start_date,
                end: event.start_date,
                id: event.companyEvent.id,
                groupId: this.companyEventCalendarManager.companyEventTypes[event.companyEvent.companyEventType.name].groupId,
                extendedProps: {
                  eventNum: i,
                  type: event.companyEvent.companyEventType.name,
                  sponsorship_amount: event.companyEvent.sponsorship_amount,
                  cosponsor: event.companyEvent.cosponsor,
                  is_cosponsored: event.companyEvent.is_cosponsored,
                  is_sponsored: event.companyEvent.is_sponsored,
                  is_cancelled: event.companyEvent.is_cancelled,
                  is_postponed: event.companyEvent.is_postponed,
                  startTime: startTime,
                  endTime: endTime,
                  timezone: event.timezone !== null ? this.dateTime.timezones.filter((tz) => tz.tz == event.timezone)[0].abbr : null,
                  location: {
                    city: event.companyEvent.companyEventAddresses.length > 0 ? event.companyEvent.companyEventAddresses[0].address.zipCode.city : null,
                    state: event.companyEvent.companyEventAddresses.length > 0 ? event.companyEvent.companyEventAddresses[0]?.address.zipCode.state.abbreviation : null
                  }
                },
                ...this.companyEventCalendarManager.eventStyling(event.companyEvent.companyEventType.name)
              }
              if(event.start_time==null && event.end_time==null){
                eventObj = {
                  ...eventObj,
                }
              }
              eventsArr.push(eventObj)
            }
            if(event.end_date!==null && !hasInactiveDate){
              let eventObj:any = {
                title: event.companyEvent.title,
                id: event.companyEvent.id,
                start: event.start_date,
                end: this.datePipe.transform(event.end_date + ' ' + endTimeAppend, 'yyyy-MM-ddTHH:mm:ss'),
                extendedProps: {
                  eventNum: i,
                  type: event.companyEvent.companyEventType.name,
                  sponsorship_amount: event.companyEvent.sponsorship_amount,
                  is_sponsored: event.companyEvent.is_sponsored,
                  cosponsor: event.companyEvent.cosponsor,
                  is_cosponsored: event.companyEvent.is_cosponsored,
                  is_cancelled: event.companyEvent.is_cancelled,
                  is_postponed: event.companyEvent.is_postponed,
                  startTime: startTime,
                  endTime: endTime,
                  location: {
                    city: event.companyEvent.companyEventAddresses.length > 0 ? event.companyEvent.companyEventAddresses[0].address.zipCode.city: null,
                    state: event.companyEvent.companyEventAddresses.length > 0 ? event.companyEvent.companyEventAddresses[0].address.zipCode.state.abbreviation : null
                  },
                  timezone: event.timezone !== null ? this.dateTime.timezones.filter((tz) => tz.tz == event.timezone)[0].abbr : null,
                },
                ...this.companyEventCalendarManager.eventStyling(event.companyEvent.companyEventType.name)
              }

              eventsArr.push(eventObj)
            }

        }
        this.calendarOptions.events = eventsArr
        setTimeout(() => {
          this.persistToggle()
        }, 50)
        querySubscription.unsubscribe();
      }
    )
  }

  eventClick(arg:any){
    // if(this.tooltip) this.tooltip.remove();
    this.tooltip?.remove()
    this.router.navigate(["/company_events/" + arg.event.id])
  }

  addEventAction(){
      let dialogRef = this.dialog.open(AddCompanyEventModalComponent,{
        width: "600px",
        data: {
          eventTypes: this.companyEventCalendarManager.typesList()
        }
      })
      const dialogSaveCloseSubscription = dialogRef.componentInstance.onSaveAndClose.subscribe((data:any) => {
        let formResponse = data.formData;
        let formGroup = data.formGroup;
        this.companyEventCalendarManager.companyEventMutation(formResponse, (data) => {
          let response:any = data;
          this.companyEventCalendarManager.companyEventDateTimeMutation(formResponse, response.createCompanyEvent.id,{outlookType: this.outlookEventType, outlookCalendar: this.outlookCalendar}, (data)=>{
            dialogRef.close()
            setTimeout(() => {
              this.getCalendarEventDates(this.start, this.end);
            }, 2000)
          }, {start: this.start, end: this.end})
        })

      })

      const dialogSaveAndResetSubscription = dialogRef.componentInstance.onSave.subscribe((data:any) => {
        let formResponse = data.formData;
        let formGroup = data.formGroup;
        this.companyEventCalendarManager.companyEventMutation(formResponse, (data) => {
          let response:any = data;
          let calendarData = {
            outlookType : this.outlookEventType,
            outlookCalendar : this.outlookCalendar
          }
          this.companyEventCalendarManager.companyEventDateTimeMutation(formResponse, response.createCompanyEvent.id, calendarData, (data)=> {
            formGroup.reset()
            dialogRef.componentInstance.isLoading = false;
            dialogRef.componentInstance.resetForm(formGroup)
            setTimeout(() => {
              this.getCalendarEventDates(this.start, this.end);
            }, 2000)
          }, {start: this.start, end: this.end})
        })
      })
  }


  toggleGroup(key: string): boolean {
    let events = this.calendarComponent.getApi().getEvents();
    let display = this.companyEventCalendarManager.toggleEventDisplay(key);
    for (let i = 0; i < events.length; i++) {
      let event = events[i];
      let group = this.companyEventCalendarManager.companyEventTypes[key];
      if (event.groupId === group.groupId) {
        event.setProp('display', display);
        return false;
      }
    }
    return false;
  }

  persistToggle(){
    let typeDetails = this.companyEventCalendarManager.companyEventTypes;
    let events = this.calendarComponent.getApi().getEvents();
    for(let i = 0; i < Object.keys(typeDetails).length; i++){
      let key = Object.keys(typeDetails)[i]
      if(typeDetails[key].display=='none'){
        let event = events.find((evt:any) => evt.groupId === typeDetails[key].groupId)
        event?.setProp('display', 'none');
      }
    }
  }

  toList(){
    this.router.navigate(['/company_events/list'])
  }










}
